import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountDrawerStore } from '@hero/hooks-shared';
import { useAccountApi } from '@hero/api-hooks';
import type { Account, Id } from '@hero/schema';
import { getAccountCounterparty } from '@hero/core';
import { StackedDrawer } from '../../../app/Drawer/StackedDrawer';
import { DrawerHeader } from '../../../app/Drawer/DrawerHeader';
import { DrawerTabs } from '../../../app/Drawer/DrawerTabs';
import { DrawerContent } from '../../../app/Drawer/DrawerContent';
import { AccountSummaryTab } from './AccountSummaryTab';
import { AccountInvoicesTab } from './AccountInvoicesTab';
import { AccountTransactionsTab } from './AccountTransactionsTab';

/**
 * Viewer's role on the account, mapped to the legacy
 * `'customer' | 'carrier'` axis the embedded `InvoiceTableManager`
 * still expects. We use 'auto' when the parent can pass
 * `viewerOrgId` so we can derive role-relative phrasing.
 */
export type AccountDrawerAsRole = 'buyer' | 'seller' | 'auto';

export interface AccountDrawerProps {
  /** Defaults to `'auto'` so the drawer picks the viewer side. */
  asRole?: AccountDrawerAsRole;
  /** Viewer org id; required when `asRole === 'auto'`. */
  viewerOrgId?: Id | string;
  syncUrl?: boolean | string;
}

interface AccountDrawerContentProps {
  account: Account;
  asRole: 'buyer' | 'seller';
  viewerOrgId?: Id | string;
  onClose: () => void;
}

const resolveAsRole = (
  props: Pick<AccountDrawerProps, 'asRole' | 'viewerOrgId'>,
  account?: Account,
): 'buyer' | 'seller' => {
  if (props.asRole === 'buyer' || props.asRole === 'seller') return props.asRole;
  // Default: the viewer is shown as the seller side of the embedded
  // invoice surface so the "Upload proof" affordance lights up by
  // default in carrier-dashboard contexts. Hosts that want the
  // buyer-leaning surface (customer-dashboard CRM) should pass
  // `asRole="buyer"`.
  return 'seller';
};

// Map the bilateral asRole back onto the legacy
// `'customer' | 'carrier'` axis required by `InvoiceTableManager`.
// Will be removed once that manager fully accepts `asRole`.
const asRoleToDisplayForUser = (role: 'buyer' | 'seller'): 'customer' | 'carrier' =>
  role === 'buyer' ? 'customer' : 'carrier';

const AccountDrawerContent: React.FC<AccountDrawerContentProps> = ({
  account,
  asRole,
  viewerOrgId,
  onClose,
}) => {
  const { t } = useTranslation(['m-account']);
  const [activeTab, setActiveTab] = useState('summary');

  const counterparty = getAccountCounterparty(account, viewerOrgId);
  const counterpartyName = counterparty?.name ?? '—';

  const tabs: { id: string; label: string }[] = [
    {
      id: 'summary',
      label: t('drawer.tabs.summary', { defaultValue: 'Summary' }),
    },
    {
      id: 'invoices',
      label: t('drawer.tabs.invoices', { defaultValue: 'Invoices' }),
    },
    {
      id: 'transactions',
      label: t('drawer.tabs.transactions', { defaultValue: 'Transactions' }),
    },
  ];

  const renderTab = () => {
    switch (activeTab) {
      case 'summary':
        return <AccountSummaryTab account={account} viewerOrgId={viewerOrgId} />;
      case 'invoices':
        return (
          <AccountInvoicesTab
            account={account}
            displayForUser={asRoleToDisplayForUser(asRole)}
          />
        );
      case 'transactions':
        return <AccountTransactionsTab account={account} />;
      default:
        return null;
    }
  };

  return (
    <>
      <DrawerHeader
        title={counterpartyName}
        subtitle={t('drawer.subtitle', {
          defaultValue: 'Bilateral account',
          currency: account.currency,
        })}
        onClose={onClose}
      />
      <DrawerTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <DrawerContent>{renderTab()}</DrawerContent>
    </>
  );
};

/**
 * Self-contained Account Drawer.
 *
 * Mount ONE instance per host page (e.g. `CRMWorkspacePage`).
 * Open from anywhere via:
 * ```ts
 * useAccountDrawerStore.getState().open(accountId)
 * ```
 */
export const AccountDrawer: React.FC<AccountDrawerProps> = props => (
  <StackedDrawer<Account>
    useStore={useAccountDrawerStore}
    useItemApi={id => useAccountApi(id)}
    syncUrl={props.syncUrl}
    defaultParamKey="accountId"
    sheetClassName="flex flex-col p-0 bg-white [&>button[class*='absolute']]:hidden"
    title="Account Details"
    description="View bilateral account information"
    renderContent={(account, onClose) => (
      <AccountDrawerContent
        account={account}
        asRole={resolveAsRole(props, account)}
        viewerOrgId={props.viewerOrgId}
        onClose={onClose}
      />
    )}
  />
);
